import { useEffect, useReducer, useRef, useState } from "react";
import { LIVES_PER_LEVEL, starsFor } from "@/game/gameData";
import { useSound } from "@/game/useSound";
import { completeLevel } from "@/game/api";
import { warn } from "@/game/log";
import { newGame, makeNorm } from "@/game/logic";
import { step, fire, beginWave, summarize } from "@/game/engine";

const freshGame = () => ({ ...newGame(), lives: LIVES_PER_LEVEL, startTime: performance.now() });

const saveProgress = async (player, res, setPlayer) => {
  try {
    const updated = await completeLevel(player.name, {
      level: res.level,
      score: res.score,
      wpm: res.wpm,
      accuracy: res.accuracy,
      won: res.type === "complete",
      stars: res.stars,
    });
    setPlayer(updated);
  } catch (err) {
    warn("Could not save progress:", err?.message);
  }
};

// owns the mutable game state, the rAF loop and the keyboard → game wiring
export function useGameLoop({ level, practice, player, setPlayer, onExit, soundEnabledRef }) {
  const norm = makeNorm(level);
  const g = useRef(freshGame());
  const [, force] = useReducer((n) => n + 1, 0);
  const [result, setResult] = useState(null);
  const endedRef = useRef(false);
  const rafRef = useRef();
  const lastRef = useRef(0);
  const sfx = useSound(soundEnabledRef);
  const latest = useRef({});
  const ctx = { level, practice, sfx, norm };

  const endGame = (type) => {
    if (endedRef.current) return;
    endedRef.current = true;
    cancelAnimationFrame(rafRef.current);
    const res = summarize(g.current, type, level);
    res.stars = type === "complete" ? starsFor(res.accuracy) : 0;
    setResult(res);
    if (type === "over") sfx.lose();
    else sfx.win();
    if (!practice) saveProgress(player, res, setPlayer);
  };

  const finishOrExit = () => {
    if (practice && g.current.phase !== "intro") {
      endGame("practice");
      force();
    } else onExit(player);
  };

  const startWave = () => {
    beginWave(g.current, ctx);
    force();
  };

  const onFire = (s) => {
    if (s.phase === "intro") beginWave(s, ctx);
    else if (fire(s, ctx) === "complete") endGame("complete");
  };

  const onChar = (s, key) => {
    if (s.phase === "intro" || s.typed.length >= 40) return;
    s.typed += key;
    s.typedChars++;
    sfx.click();
  };

  const handleKey = (e) => {
    if (endedRef.current) return;
    const s = g.current;
    if (e.key === "Escape") return finishOrExit();
    if (e.key === "Enter" || e.key === " ") onFire(s);
    else if (e.key === "Backspace") s.typed = s.typed.slice(0, -1);
    else if (e.key.length === 1) onChar(s, e.key);
    else return;
    e.preventDefault();
    force();
  };

  const startLoop = () => {
    g.current = freshGame();
    endedRef.current = false;
    setResult(null);
    lastRef.current = performance.now();
    const loop = (now) => {
      if (endedRef.current) return;
      const dt = Math.min((now - lastRef.current) / 1000, 0.05);
      lastRef.current = now;
      if (step(g.current, dt, now, latest.current.ctx) === "over") return latest.current.endGame("over");
      force();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  };

  // always points at the newest closures so the mount-only effects never go stale
  latest.current = { ctx, endGame, handleKey, startLoop, resume: sfx.resume };

  useEffect(() => {
    latest.current.resume();
    latest.current.startLoop();
    return () => cancelAnimationFrame(rafRef.current);
  }, [level.id]);

  useEffect(() => {
    const onKey = (e) => latest.current.handleKey(e);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return { s: g.current, norm, result, restart: () => latest.current.startLoop(), startWave, finishOrExit };
}
